import React from 'react';
import styled from 'styled-components';

import { Skeleton } from '@material-ui/lab';

import { VenuePlan } from '../../graphql/API';
import { useAdminGetDashboardVenuesStatsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardVenuesStatsDataQuery';
import { calculatePercentageChange } from '../../utils/dashboard';
import { VENUE_MANUAL_TAGS } from '../../utils/manual-tags';
import ActiveCard from './ActiveCard';
import { PackageCard } from './PackageCard';

type Props = {
  fromDate: string | null;
  priorFrom: string | null;
  toDate: string | null;
  priorTo: string | null;
  selectedLabel: string;
  marketID: string | null;
};

export const VenuesV2: React.FC<Props> = ({
  fromDate,
  priorFrom,
  priorTo,
  toDate,
  selectedLabel,
  marketID,
}) => {
  const { data, isFetching } = useAdminGetDashboardVenuesStatsDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
    marketID: marketID || '',
  });

  return (
    <PackageContainerDesktopVenueSection>
      {isFetching && (
        <>
          <Skeleton
            animation="wave"
            variant="rect"
            width={'100%'}
            height={100}
            style={{ borderRadius: '8px' }}
          />
          <Skeleton
            animation="wave"
            variant="rect"
            width={'100%'}
            height={100}
            style={{ borderRadius: '8px' }}
          />
          <Skeleton
            animation="wave"
            variant="rect"
            width={'100%'}
            height={100}
            style={{ borderRadius: '8px' }}
          />
          <Skeleton
            animation="wave"
            variant="rect"
            width={'100%'}
            height={100}
            style={{ borderRadius: '8px' }}
          />
        </>
      )}
      {!isFetching && data ? (
        <>
          <ActiveCard
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#E6EAFD"
            count={String(data?.totalActiveVenues)}
            label="Active venues"
            link={`/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}`}
            comparison={calculatePercentageChange(
              data?.totalCurrentActiveVenues || 0,
              data?.totalPriorActiveVenues || 0,
            )}
          />
          <PackageCard
            link={`/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.free}`}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={String(data?.totalFreeVenuesWithoutOffers)}
            label="Free"
            newCount={data?.totalNewFreeVenues || 0}
            canceledCount={data?.totalExpiredFreeVenues || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentFreeVenuesWithoutOffers || 0,
              data?.totalPriorFreeVenuesWithoutOffers || 0,
            )}
          />
          <PackageCard
            link={`/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.starter}`}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={String(data?.totalStarterVenuesWithoutOffers)}
            label="Starter"
            substr={String(data?.totalStarterFreeVenues)}
            newCount={data?.totalNewStarterVenues || 0}
            canceledCount={data?.totalExpiredStarterVenues || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentStarterVenuesWithoutOffers || 0,
              data?.totalPriorStarterVenuesWithoutOffers || 0,
            )}
          />
          <PackageCard
            link={`/businesses?tags=${VENUE_MANUAL_TAGS.VenuePlans}&notInTags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.plus}`}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={String(data?.totalPlusVenuesWithoutOffers)}
            label="Plus"
            substr={String(data?.totalPlusFreeVenues)}
            newCount={data?.totalNewPlusVenues || 0}
            canceledCount={data?.totalExpiredPlusVenues || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentPlusVenuesWithoutOffers || 0,
              data?.totalPriorPlusVenuesWithoutOffers || 0,
            )}
          />
          <ActiveCard
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#EEE2F8"
            count={String(data?.totalActiveBrands)}
            label="Active brands subscribed"
            link={`/businesses?tags=${VENUE_MANUAL_TAGS.Online}`}
            comparison={calculatePercentageChange(
              data?.totalCurrentActiveBrands || 0,
              data?.totalPriorActiveBrands || 0,
            )}
          />
          <PackageCard
            link={`/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.basic}`}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={String(data?.totalBasicBrandsWithoutOffers)}
            label="Basic"
            newCount={data?.totalNewBasicBrands || 0}
            substr={String(data?.totalBasicFreeBrands || 0)}
            canceledCount={data?.totalExpiredBasicBrands || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentBasicBrandsWithoutOffers || 0,
              data?.totalPriorBasicBrandsWithoutOffers || 0,
            )}
          />
          <PackageCard
            link={`/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.starter}`}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={String(data?.totalStarterBrandsWithoutOffers)}
            label="Starter"
            newCount={data?.totalNewStarterBrands || 0}
            substr={String(data?.totalStarterFreeBrands || 0)}
            canceledCount={data?.totalExpiredStarterBrands || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentStarterBrandsWithoutOffers || 0,
              data?.totalPriorStarterBrandsWithoutOffers || 0,
            )}
          />
          <PackageCard
            link={`/businesses?tags=${VENUE_MANUAL_TAGS.Online}&plan=${VenuePlan.plus}`}
            showFooter
            showComparison
            selectedLabel={selectedLabel}
            bgColor="#FDF1E6"
            count={String(data?.totalPlusBrandsWithoutOffers)}
            label="Plus"
            newCount={data?.totalNewPlusBrands || 0}
            substr={String(data?.totalPlusFreeBrands || 0)}
            canceledCount={data?.totalExpiredPlusBrands || 0}
            comparison={calculatePercentageChange(
              data?.totalCurrentPlusBrandsWithoutOffers || 0,
              data?.totalPriorPlusBrandsWithoutOffers || 0,
            )}
          />
        </>
      ) : null}
    </PackageContainerDesktopVenueSection>
  );
};

const PackageContainerDesktopVenueSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 16px;
  padding-top: 80px;
  padding-bottom: 60px;
`;
